/**
 * @name useFullscreen
 * @description Hook para gerenciar o modo de tela cheia do navegador com suporte a "trava"
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/hooks/useFullscreen.ts
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface UseFullscreenReturn {
  /** Indica se está em modo de tela cheia */
  isFullscreen: boolean;
  /** Indica se o navegador suporta tela cheia */
  isSupported: boolean;
  /** Indica se a tela cheia está travada */
  isLocked: boolean;
  /** Entra em modo de tela cheia */
  enterFullscreen: () => Promise<boolean>;
  /** Sai do modo de tela cheia (ignora se estiver travado) */
  exitFullscreen: () => Promise<boolean>;
  /** Alterna entre tela cheia e normal */
  toggleFullscreen: () => Promise<boolean>;
  /** Trava a tela cheia (impede o usuário de sair) */
  lockFullscreen: () => void;
  /** Destrava e sai da tela cheia */
  unlockAndExitFullscreen: () => Promise<boolean>;
}

/**
 * Hook para controlar o modo de tela cheia do navegador
 * Com suporte a "trava" para totens e quiosques
 */
export const useFullscreen = (): UseFullscreenReturn => {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const isLockedRef = useRef<boolean>(false); // Ref para acesso síncrono no listener
  const reenterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Verifica se o navegador suporta a API de tela cheia
  const isSupported = Boolean(
    document.documentElement.requestFullscreen ||
      (document.documentElement as any).webkitRequestFullscreen ||
      (document.documentElement as any).mozRequestFullScreen ||
      (document.documentElement as any).msRequestFullscreen,
  );

  // Função para verificar se está em tela cheia
  const checkFullscreenStatus = useCallback(() => {
    const fullscreenElement =
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement;

    setIsFullscreen(Boolean(fullscreenElement));
    return Boolean(fullscreenElement);
  }, []);

  // Entrar em modo de tela cheia
  const enterFullscreen = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      console.warn("⚠️ Fullscreen API não é suportada neste navegador");
      return false;
    }

    try {
      const element = document.documentElement;

      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }

      console.log("✅ Modo tela cheia ativado");
      return true;
    } catch (error) {
      console.error("❌ Erro ao entrar em tela cheia:", error);
      return false;
    }
  }, [isSupported]);

  // Sair do modo de tela cheia (só funciona se não estiver travado)
  const exitFullscreen = useCallback(async (): Promise<boolean> => {
    if (isLockedRef.current) {
      console.log("🔒 Tela cheia está travada, não é possível sair");
      return false;
    }

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }

      console.log("✅ Modo tela cheia desativado");
      return true;
    } catch (error) {
      console.error("❌ Erro ao sair da tela cheia:", error);
      return false;
    }
  }, []);

  // Alternar entre tela cheia e normal
  const toggleFullscreen = useCallback(async (): Promise<boolean> => {
    if (isFullscreen) {
      return exitFullscreen();
    } else {
      return enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  // Travar tela cheia
  const lockFullscreen = useCallback(() => {
    setIsLocked(true);
    isLockedRef.current = true;
    console.log("🔒 Tela cheia TRAVADA - usuário não pode sair");
  }, []);

  // Destravar e sair da tela cheia
  const unlockAndExitFullscreen = useCallback(async (): Promise<boolean> => {
    console.log("🔓 Destravando tela cheia...");
    setIsLocked(false);
    isLockedRef.current = false;

    // Limpar qualquer timeout pendente
    if (reenterTimeoutRef.current) {
      clearTimeout(reenterTimeoutRef.current);
      reenterTimeoutRef.current = null;
    }

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }

      console.log("✅ Tela cheia destravada e desativada");
      return true;
    } catch (error) {
      console.error("❌ Erro ao sair da tela cheia:", error);
      return false;
    }
  }, []);

  // Listener para mudanças no estado de tela cheia
  useEffect(() => {
    checkFullscreenStatus();

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = checkFullscreenStatus();

      // Se saiu da tela cheia mas está travado, reentrar automaticamente
      if (!isCurrentlyFullscreen && isLockedRef.current) {
        console.log(
          "🔄 Tentativa de sair detectada - reentrando em tela cheia...",
        );

        // Limpar timeout anterior se existir
        if (reenterTimeoutRef.current) {
          clearTimeout(reenterTimeoutRef.current);
        }

        // Pequeno delay para evitar conflitos com o browser
        reenterTimeoutRef.current = setTimeout(async () => {
          if (isLockedRef.current) {
            try {
              const element = document.documentElement;

              if (element.requestFullscreen) {
                await element.requestFullscreen();
              } else if ((element as any).webkitRequestFullscreen) {
                await (element as any).webkitRequestFullscreen();
              } else if ((element as any).mozRequestFullScreen) {
                await (element as any).mozRequestFullScreen();
              } else if ((element as any).msRequestFullscreen) {
                await (element as any).msRequestFullscreen();
              }

              console.log("✅ Reentrou em tela cheia automaticamente");
            } catch (error) {
              console.warn(
                "⚠️ Não foi possível reentrar em tela cheia:",
                error,
              );
            }
          }
        }, 100);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );

      // Limpar timeout ao desmontar
      if (reenterTimeoutRef.current) {
        clearTimeout(reenterTimeoutRef.current);
      }
    };
  }, [checkFullscreenStatus]);

  return {
    isFullscreen,
    isSupported,
    isLocked,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    lockFullscreen,
    unlockAndExitFullscreen,
  };
};

export default useFullscreen;
